import { HandleClientError } from './client-error-service';
import qs from 'qs';

export interface DictionaryItem {
	key: string;
	value: string;
}

export interface ProgramRecord {
	programId: string;
	name: string;
	handlesBackgroundCheckInternally: boolean;
}

function getAntiForgeryToken(): string {
	return (document.querySelector('[Name="AntiForgeryToken"]') as HTMLInputElement).value;
}

//represents a code table option object
export interface CodeTableOption {
	id: string;
	code: string;
	description: string;
	isActive: boolean;
}

//Fetches the set of programs active on a given date
export async function getCurrentPrograms(date: string): Promise<DictionaryItem[]> {
	const queryParams = qs.stringify({activeDate: date});

	const programs = await fetch(`/clientData/currentPrograms?${queryParams}`)
		.then((res) => res.json())
		.catch(e => HandleClientError(getCurrentPrograms.name, e));

	return programs;
}

//Fetches the services types for a set of program ids
export async function getCurrentServiceTypesForInvoicePeriod(invoicePeriod: string, ...ids: string[]): Promise<CodeTableOption[]> {
	const queryParams = qs.stringify({programIds: ids, invoicePeriod: invoicePeriod});

	const serviceTypes = await fetch(`/clientData/currentServiceTypeOptionsForInvoicePeriod?${queryParams}`)
		.then((res) => res.json())
		.catch(e => HandleClientError(getCurrentServiceTypesForInvoicePeriod.name, e));

	return serviceTypes;
}

//Fetches the active service types for a given program and date
export async function getCurrentServiceTypesForProgram(activeDate: string, programId: string): Promise<DictionaryItem[]> {
	const queryParams = qs.stringify({activeDate: activeDate, programId: programId});

	const serviceTypes = await fetch(`/clientData/activeServiceTypeOptions?${queryParams}`)
		.then((res) => res.json())
		.catch(e => HandleClientError(getCurrentServiceTypesForProgram.name, e));

	return serviceTypes;
}

//Fetches the counties
export async function getCounties(): Promise<CodeTableOption[]> {
	const counties = await fetch('/clientData/counties')
		.then((res) => res.json())
		.catch(e => HandleClientError(getCounties.name, e));

	return counties;
}

//Fetches the unacknowledged user notifications
export async function getUserNotificationCount(): Promise<number> {
	const userNotifications = await fetch('/clientData/userNotifications')
		.then((res) => res.json())
		.catch(e => HandleClientError(getUserNotificationCount.name, e));

	return userNotifications.length;
}

//Fetches programs
export async function getProgramsById(programIds: string[]): Promise<Array<ProgramRecord>> {
	const params = new URLSearchParams();

	programIds.forEach((programId, index) => {
		params.append(`programIds[${index}]`, programId);
	});

	const url = '/clientData/programsById?' + params.toString();
	const programs = await fetch(url)
		.then((res) => res.json())
		.catch(e => HandleClientError(getProgramsForProviders.name, e));

	return programs;
}

//Fetches the programs
export async function getProgramsByProvider(providerId: string): Promise<CodeTableOption[]> {
	const programs = await fetch('/clientData/programs?providerid=' + providerId)
		.then((res) => res.json())
		.catch(e => HandleClientError(getProgramsByProvider.name, e));

	return programs;
}

//Fetches the programs
export async function getProgramsAsDictionary(providerIds: string[]): Promise<DictionaryItem[]> {
	let qs = '';
	if (providerIds != null) {
		providerIds.forEach((id) => (qs += '&providerIds=' + id));
		qs = '?' + qs.substr(1);
	}
	const programs = await fetch('/clientData/providersprograms' + qs)
		.then((res) => res.json())
		.catch(e => HandleClientError(getProgramsAsDictionary.name, e));

	return programs;
}

// Fetches Judicial districts
export async function getJudicialDistricts(): Promise<DictionaryItem[]> {
	const jds = await fetch('/clientData/judicialDistricts')
		.then((res) => res.json())
		.catch(e => HandleClientError(getJudicialDistricts.name, e));

	return jds;
}

export async function getJudicialDistrictsForInvoicePeriod(period: string, programIds: string[], serviceTypeIds: string[]): Promise<DictionaryItem[]> {
	const params = qs.stringify({ period: period, programIds: programIds, serviceTypeIds: serviceTypeIds });
	const url = `/clientData/judicialDistrictsForInvoicePeriod?${params}`;

	const jds = await fetch(url)
		.then((res) => res.json())
		.catch(e => HandleClientError(getJudicialDistrictsForInvoicePeriod.name, e));

	return jds;
}

//Fetches the programs for multiple providers
export async function getProgramsForProviders(providerIds: string[]): Promise<DictionaryItem[]> {
	const params = new URLSearchParams();

	providerIds.forEach((providerId, index) => {
		params.append(`providerIds[${index}]`, providerId);
	});

	const url = '/clientData/programs?' + params.toString();
	const programs = await fetch(url)
		.then((res) => res.json())
		.catch(e => HandleClientError(getProgramsForProviders.name, e));

	return programs;
}

export async function checkPasswordHistory(username: string, password: string, resetToken: string): Promise<boolean> {
	const response = await fetch('/clientData/checkPassword', {
		method: 'post',
		body: JSON.stringify({
			username: username,
			password: password,
			resetToken: resetToken,
		}),
		headers: {
			'Content-Type': 'application/json',
			'X-CSRF-ANTIFORGERY-TOKEN': getAntiForgeryToken(),
		},
	});

	const isRecentPassword = await response.json().catch(e => HandleClientError(checkPasswordHistory.name, e));

	return !isRecentPassword;
}

//Fetches the users for a give program id
export async function getProgramUsers(id: string): Promise<DictionaryItem[]> {
	const programUsers = await fetch(`/clientData/programUsers?programId=${id}`)
		.then((res) => res.json())
		.catch(e => HandleClientError(getProgramUsers.name, e));

	return programUsers;
}

//Fetches the legal status codes for a given service type id
export async function getLegalStatusCodesForServiceType(ids: string[]): Promise<DictionaryItem[]> {
    const legalStatuses = await fetch(`/clientData/legalStatuses?serviceTypeId=${ids.join('&serviceTypeId=')}`)
        .then((res) => res.json())
        .catch(e => HandleClientError(getLegalStatusCodesForServiceType.name, e));

    return legalStatuses;
}

export async function getCurrentLevelCodesForServiceType(id: string, activeDate: string): Promise<DictionaryItem[]> {
	const levelCodeItems = await fetch(`/clientData/levelCodesForServiceType?serviceTypeId=${id}&activeDate=${activeDate}`)
		.then((res) => res.json())
		.catch(e => HandleClientError(getCurrentLevelCodesForServiceType.name, e));

	return levelCodeItems;
}

export async function getCurrentRateAdjustmentCodesForServiceType(id: string, activeDate: string): Promise<CodeTableOption[]> {
	const rateAdjustmentItems = await fetch(`/clientData/rateAdjustmentCodesForServiceType?serviceTypeId=${id}&activeDate=${activeDate}`)
		.then((res) => res.json())
		.catch(e => HandleClientError(getCurrentRateAdjustmentCodesForServiceType.name, e));

	return rateAdjustmentItems;
}

export async function getRoleDescriptions(): Promise<DictionaryItem[]> {
    const roleInfo = await fetch(`/clientData/roleDescriptions`)
        .then((res) => res.json())
        .catch(e => HandleClientError(getPageHelpTexts.name, e));

    return roleInfo;
}

export async function getPageHelpTexts(pageId: string): Promise<DictionaryItem[]> {
    const helpTexts = await fetch(`/clientData/helpTexts?pageId=${pageId}`)
        .then((res) => res.json())
        .catch(e => HandleClientError(getPageHelpTexts.name, e));

    return helpTexts;
}

export async function savePageHelpText(pageId: string, fieldId: string, helpText: string): Promise<boolean> {
	return fetch(`/clientData/updateHelpText`, 
		{ 
			body: JSON.stringify({ pageId, fieldId, helpText }),
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-ANTIFORGERY-TOKEN': (document.querySelector('[Name="AntiForgeryToken"]') as HTMLInputElement).value,
			}
		})
		.then(async (res) => !!res.json())
		.catch((e) => false);
}

export async function getReleaseToOptions(serviceTypeCategoryId: string, terminationReasonId: string): Promise<DictionaryItem[]> {
    const options = await fetch(`/clientData/releaseToOptions?serviceTypeCategoryId=${serviceTypeCategoryId}&terminationReasonId=${terminationReasonId}`)
        .then((res) => res.json())
        .catch(e => HandleClientError(getReleaseToOptions.name, e));

    return options;
}

export async function getTreatmentFundTypes(serviceDates: string[], stayId: string, fundCategoryId: string): Promise<CodeTableOption[]> {
	const params = qs.stringify({ serviceDates: serviceDates, stayId: stayId, fundCategoryId: fundCategoryId });
	const url = `/clientData/treatmentFundTypes?${params}`;

    const subTypes = await fetch(url)
		.then((res) => res.json())
		.catch(e => HandleClientError(getTreatmentFundTypes.name, e));

	return subTypes;
}

export async function getTreatmentFundSubTypes(serviceDates: string[], stayId: string, fundTypeId: string, fundCategoryId: string): Promise<CodeTableOption[]> {
	const params = qs.stringify({ serviceDates: serviceDates, stayId: stayId, fundTypeId: fundTypeId, fundCategoryId: fundCategoryId });
	const url = `/clientData/treatmentFundSubTypes?${params}`;

    const subTypes = await fetch(url)
		.then((res) => res.json())
		.catch(e => HandleClientError(getTreatmentFundSubTypes.name, e));

	return subTypes;
}

export async function getTreatmentFundProviders(fundSubTypeId: string, fundTypeId: string, fundCategoryId: string): Promise<CodeTableOption[]> {
	const params = qs.stringify({ fundSubTypeId: fundSubTypeId, fundTypeId: fundTypeId, fundCategoryId: fundCategoryId });
	const url = `/clientData/treatmentFundProviders?${params}`;

    const subTypes = await fetch(url)
		.then((res) => res.json())
		.catch(e => HandleClientError(getTreatmentFundProviders.name, e));

	return subTypes;
}

export async function getReportGroups(): Promise<CodeTableOption[]> {
	const url = '/clientData/reportGroups';

    const reportGroups = await fetch(url)
		.then((res) => res.json())
		.catch(e => HandleClientError(getReportGroups.name, e));

	return reportGroups;
}

export async function getAssociatedReportGroups(devExReportId: string): Promise<CodeTableOption[]> {
	const params = qs.stringify({ devExReportId: devExReportId });
	const url = `/clientData/associatedReportGroups?${params}`;

    const reportGroups = await fetch(url)
		.then((res) => res.json())
		.catch(e => HandleClientError(getAssociatedReportGroups.name, e));

	return reportGroups;
}

export async function regenServiceInvoiceGroup(serviceInvoiceGroupId: string): Promise<void> {
	const response = await fetch('/admin/RegenInvoiceGroup', {
		method: 'post',
		body: JSON.stringify({
			serviceInvoiceGroupId: serviceInvoiceGroupId,
		}),
		headers: {
			'Content-Type': 'application/json',
			'X-CSRF-ANTIFORGERY-TOKEN': getAntiForgeryToken(),
		},
	});

	await response.text()
		.catch(e => console.log(e));
}

export async function acknowledgeStayNotifications(): Promise<void> {
	const urlParams = new URLSearchParams(window.location.search);
	const stayId = urlParams.get('stayId');
	var jsonPayload = JSON.stringify(stayId);

	if (stayId) {
		await fetch(`/Clients/Intake/AcknowledgeStayNotifications`, {
			method: 'POST', // Change to POST
			headers: {
				'Content-Type': 'application/json', // Set the content type to JSON
				'X-CSRF-ANTIFORGERY-TOKEN': getAntiForgeryToken(),
			},
			body: jsonPayload, // Pass the stayId in the request body
		})
			.then((response) => {
				if (!response.ok) {
					console.error('Failed to acknowledge stay notifications:', response);
				}
			})
			.catch((error) => {
				console.error('Error while acknowledging stay notifications:', error);
			});
	} else {
		console.warn('No stayId found in the query string.');
	}
}


(<any>window).regenServiceInvoiceGroup = regenServiceInvoiceGroup;